<template>
    <v-form @submit.prevent="updatePayrollDetails" ref="form">
        <v-card class="rounded-4">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">Edit Employment</span>
                <v-spacer></v-spacer>
                <v-btn icon @click.native="closeDialog()" >
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert>  
            <v-card-text>
                <v-layout v-if="isUserProfileUpdate" row wrap>
                    <v-flex xs12 sm6 class="form-item">
                        <v-select
                            :items="employmentTypes"
                            :rules="[rules.required]"
                            item-text="description"
                            item-value="employmentType"
                            label="Employment Type"
                            :disabled="disabled"
                            v-model="employmentType"
                        ></v-select>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-item">
                        <v-select
                            :items="employmentStatusTypes"
                            :rules="[rules.required]"
                            item-text="description"
                            item-value="employmentStatus"
                            label="Employment Status"
                            :disabled="disabled"
                            v-model="employmentStatus"
                            @change="updateEndDate"
                        ></v-select>
                    </v-flex>
                </v-layout>
                <v-layout v-if="isUserProfileUpdate" row wrap>
                    <v-flex xs12 sm6 class="form-item">
                        <v-text-field label="Position"
                                v-model="position"
                                :rules="[rules.required]"
                                :disabled="disabled"
                                @keyup="error=false"></v-text-field>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-item">
                        <v-select
                            :items="salaryTypes"
                            :rules="[rules.required]"
                            item-text="name"
                            item-value="salaryType"
                            label="Salary Type"
                            :disabled="disabled"
                            v-model="salaryType"
                        ></v-select>
                    </v-flex>
                </v-layout>
                <v-layout v-if="isUserProfileUpdate" row wrap>
                    <v-flex xs12 sm6 class="form-item">
                        <v-menu v-model="menuStartDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field v-model="startDate" label="Start Date" readonly v-on="on" :rules="[rules.required]" :disabled="disabled"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="startDate" @input="menuStartDate = false"></v-date-picker>
                        </v-menu>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-item">
                        <v-menu v-model="menuEndDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field v-model="endDate" label="End Date" readonly v-on="on" :disabled="disabled||!isResignedOrTerminated"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="endDate" @input="menuEndDate = false"></v-date-picker>
                        </v-menu>
                    </v-flex>
                </v-layout>
                <v-layout v-if="isUserProfileUpdate" row wrap>
                    <v-flex xs12 sm6 class="form-item">
                         <v-select
                            :items="workArrangementTypes"
                            :rules="[rules.required]"
                            item-text="description"
                            item-value="workArrangement"
                            label="Work Arrangement"
                            :disabled="disabled"
                            v-model="workArrangement"
                        ></v-select>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-item">
                        <v-autocomplete
                            label="Role Category"
                            v-model="roleCategory"
                            :disabled="disabled"
                            :items="roleCategories"
                            item-text="description"
                            item-value="roleCategory"
                            clearable
                            clear-icon="backspace"
                        >
                            <!-- DROPDOWN ITEMS -->
                            <template v-slot:item="data">
                                <!-- HEADER -->
                                <template v-if="data.item.header">
                                    <v-subheader>
                                        {{ data.item.header }}
                                    </v-subheader>
                                </template>

                                <!-- NORMAL ITEM -->
                                <template v-else>
                                    <v-list-tile-content>
                                        <v-list-tile-title>
                                            {{ data.item.description }}
                                        </v-list-tile-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>
                <v-layout row wrap>
                    <v-flex xs12 sm6 class="form-item">
                        <v-text-field label="SSS No."
                            v-model="sss"
                            :rules="[rules.sss]"
                            :disabled="disabled"
                            mask="##-#######-#"
                            @keyup="error=false"></v-text-field>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-item">
                        <v-text-field label="Philhealth No."
                            v-model="philhealth"
                            :rules="[rules.philhealth]"
                            :disabled="disabled"
                            mask="##-#########-#"
                            @keyup="error=false"></v-text-field>
                    </v-flex>
                </v-layout>
                <v-layout row wrap>
                    <v-flex xs12 sm6 class="form-item">
                        <v-text-field label="Pagibig No."
                            v-model="pagibig"
                            :rules="[rules.pagibig]"
                            :disabled="disabled"
                            mask="####-####-####"
                            @keyup="error=false"></v-text-field>
                    </v-flex>
                    <v-flex xs12 sm6 class="form-item">
                        <v-text-field label="Tax Identification No."
                            v-model="tin"
                            :rules="[rules.tin]"
                            :disabled="disabled"
                            mask="###-###-###"
                            @keyup="error=false"></v-text-field>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="mr-25">
                <v-spacer></v-spacer>
                <v-btn color="primary" type="submit" :loading="loading" dark>
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import employmentApi from '@/api/employment'

export default {
    name: 'edit-employment',
    data () {
        return {
            loading: false,
            disabled: false,
            error: false,
            errorMessage: '',

            //todo - move this to rules-helper.js
            rules: {
                required: value => !!value || 'Required.',
                min: v => v.length >= 8 || 'Min 8 characters',
                counter: value => value.length <= 20 || 'Max 20 characters',
                tin: value => {
                    const pattern = /\d{3}\d{3}\d{3}/
                    return pattern.test(value) || 'Invalid Tax Identifacation No.'
                },
                sss: value => {
                    const pattern = /\d{2}\d{7}\d{1}/
                    return pattern.test(value) || 'Invalid SSS No.'
                },
                philhealth: value => {
                    const pattern = /\d{2}\d{9}\d{1}/
                    return pattern.test(value) || 'Invalid Philhealth No.'
                },
                pagibig: value => {
                    const pattern = /\d{4}\d{4}\d{4}/
                    return pattern.test(value) || 'Invalid Pagibig No.'
                },
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || 'Invalid e-mail.'
                }
            },
            //Refer to dbo.StatusCodes where Remarks = 'Employment Type', static data for faster rendering
            employmentTypes: [
                {employmentType: 36, description: "Probationary"},
                {employmentType: 22, description: "Permanent"},
                {employmentType: 37, description: "Regular"},
                {employmentType: 23, description: "Consultant"},
                {employmentType: 24, description: "Contractor"},
                {employmentType: 24, description: "Contractor"},
                {employmentType: 87, description: "Project-Based"},
            ],
            //Refer to dbo.StatusCodes where Remarks = 'Employment', static data for faster rendering
            employmentStatusTypes: [
                {employmentStatus: 17, description: "Hiring Process"},
                {employmentStatus: 18, description: "Contract Signed"},
                {employmentStatus: 19, description: "Active"},
                {employmentStatus: 20, description: "Talent Pool"},
                {employmentStatus: 21, description: "Resigned"},
                {employmentStatus: 48, description: "Terminated"},
                {employmentStatus: 66, description: "Suspended"},
                {employmentStatus: 67, description: "On Maternity Leave"},
                {employmentStatus: 68, description: "On Sick Leave"}
            ],
            //Refer to dbo.StatusCodes where Remarks = 'Employment', static data for faster rendering
            workArrangementTypes: [
                {workArrangement: 69, description: "On-site"},
                {workArrangement: 70, description: "Remote"},
                {workArrangement: 71, description: "Hybrid"},
                {workArrangement: 72, description: "Field-Based"}
            ],
            //Refer to to dbo.SalaryTypes, static data for faster rendering
            salaryTypes: [
                {salaryType: 1, name: 'Monthly'},
                {salaryType: 2, name: 'Hourly'},
                {salaryType: 3, name: 'Daily'},
            ],
             //Refer to dbo.StatusCodes where Remarks = 'Role Category', static data for faster rendering
            roleCategories: [
                { header: 'BPO / Customer Operations' },
                { roleCategory: 73, description: 'Inbound' },
                { roleCategory: 74, description: 'Outbound' },
                { roleCategory: 75, description: 'Customer Support' },
                { roleCategory: 76, description: 'Back Office Support' },
                { roleCategory: 86, description: 'Confirmations' },

                { header: 'Healthcare / Service Delivery' },
                { roleCategory: 77, description: 'Direct Care' },
                { roleCategory: 78, description: 'Ancillary' },

                { header: 'IT / Technical' },
                { roleCategory: 79, description: 'Technical Delivery' },
                { roleCategory: 80, description: 'Quality Assurance' },
                { roleCategory: 81, description: 'DevOps / Infrastructure' },

                { header: 'Operations / Admin' },
                { roleCategory: 82, description: 'Operations' },
                { roleCategory: 83, description: 'Administrative Support' },

                { header: 'Management' },
                { roleCategory: 84, description: 'Supervisor' },
                { roleCategory: 85, description: 'Manager' }
            ],
            userId: null,
            avatar: '',
            firstName: '',
            lastName: '',
            employmentType: null,
            employmentStatus: null,
            workArrangement: null,
            roleCategory: null,
            workDaysPerWeek: null,
            workHoursPerDay: null,
            salaryType: null,
            tin: null,
            sss: null,
            philhealth: null,
            pagibig: null,
            menuStartDate: false, 
            startDate: null,
            menuEndDate: false, 
            endDate: null,
            rdo: '',
            branchCode: '',
            position: null,
            isUserProfileUpdate: null
        }
    },
    methods: {
        updateEndDate() {
            if (this.isResignedOrTerminated) {
                this.endDate = this.$moment(new Date()).format('YYYY-MM-DD')
            }else{
                this.endDate = null
            }
        },
        async updatePayrollDetails() {
            if (this.$refs.form.validate()) {
                this.loading = true
                this.disabled = true
                
                var data = {
                    userId : this.userId,
                    employmentType : this.employmentType,
                    employmentStatus: this.employmentStatus,
                    workArrangement: this.workArrangement,
                    roleCategory: this.roleCategory,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    workDaysPerWeek : this.workDaysPerWeek,
                    workHoursPerDay: this.workHoursPerDay,
                    salaryType: this.salaryType,
                    tin : this.tin,
                    sss : this.sss,
                    philhealth: this.philhealth,
                    pagibig : this.pagibig,
                    rdo: this.rdo,
                    branchCode: this.branchCode,
                    position: this.position
                }

                const response = await employmentApi.upsert(data)

                if (response && response.status == 200) {
                    this.loading = false
                    this.disabled = false
                    this.clearForm()
                    this.$emit('send', 'Edit')
                    this.$emit('update:dialogEmployment', false)
                }
            }
        },
        validateWorkHoursPerDay() {
            this.workHoursPerDay = this.workHoursPerDay > 9 ? 9 : this.workHoursPerDay
        },
        validateWorkDaysPerWeek() {
            this.workDaysPerWeek = this.workDaysPerWeek > 7 ? 7 : this.workDaysPerWeek
        },
        async setParams(isUserProfileUpdate, userData, employmentData) {
            this.isUserProfileUpdate = isUserProfileUpdate
            this.avatar = userData.avatar
            this.userId = userData.userId
            this.firstName = userData.firstName
            this.lastName = userData.lastName
            this.employmentType = employmentData.employmentType 
            this.employmentStatus = employmentData.employmentStatus
            this.workArrangement = employmentData.workArrangement
            this.roleCategory = employmentData.roleCategory
            this.startDate = employmentData.startDate?this.$moment(employmentData.startDate).format('YYYY-MM-DD'):null
            this.endDate = employmentData.endDate?this.$moment(employmentData.endDate).format('YYYY-MM-DD'):null
            this.workDaysPerWeek = employmentData.workDaysPerWeek
            this.workHoursPerDay = employmentData.workHoursPerDay
            this.salaryType = employmentData.salaryType
            this.tin = employmentData.tin
            this.sss = employmentData.sss
            this.philhealth= employmentData.philhealth
            this.pagibig = employmentData.pagibig
            this.position = employmentData.position
        },
        getemploymentType(employmentDesc) {
            var data = this.employmentType = this.employmentTypes.filter(item => {
                return item.description == employmentDesc
            })[0]
            return data.employmentType
        },
        closeDialog() {
            this.$emit('update:dialogEmployment', false)
            this.clearForm()
        },
        clearForm() {
            this.$refs.form.reset()
            this.loading = false
            this.error = false
        },
        allowedDates(val) {
            return this.$moment(Date()).format('YYYY-MM-DD') <= this.$moment(val).format('YYYY-MM-DD')
        }
    },
    computed: {
        isResignedOrTerminated() {
            return this.employmentStatusTypes
                .filter(x => x.employmentStatus == this.employmentStatus 
                    && (x.description == 'Resigned' || x.description == 'Terminated')
                ).length
        }
    },
    created() {}
}
</script>
<style scoped>
.form-item {
    padding: 0px 20px 0px 20px;
}
</style>